'use client';

import { forwardRef } from 'react';

import { css, cx } from '@/styled-system/css';

import type { UnstyledButtonPropsInterface } from './types/unstyled-button.types';

/**
 * A button element with default browser styles overwritten to produce a style
 * agnostic button.
 *
 * This component is intended to be used to provide an accessible element for performing
 * actions. For instance, `onClick` on a `span` is not accessible to keyboard-based users without
 * additional work such as having appropriate keystroke event listeners (i.e. onKeyDown for Enter).
 *
 * By utilizing a `button` DOM node as a wrapper to an `span`, the click event listener,
 * subsequently moved to the button, is invoked natively by the browser without any
 * additional attributes necessary for keyboard-based interactions.
 *
 * See [MDN documentation on `button`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button)
 * for a list of permitted content.
 *
 * @since 8.30.0
 */
export const UnstyledButton = forwardRef<HTMLButtonElement, UnstyledButtonPropsInterface>(
  (props: UnstyledButtonPropsInterface, forwardedRef) => {
    const { className, css: cssProp, role = 'button', ...rest } = props;
    const styles = css(cssProp);
    return (
      <button
        ref={forwardedRef}
        data-c11n-component="UnstyledButton"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_unstyled-button', styles, className)}
        role={role}
        type="button"
        {...rest}
      />
    );
  },
);

UnstyledButton.displayName = 'UnstyledButton';
